// MIDAS V3 — Streaming markdown (progressive prose during a stream)
//
// Mirrors V2's StreamingMarkdownV2 (ChatMessageListV2.tsx:162-219). Same
// shape, same parse-safety pipeline, same handoff behaviour. Where V2 uses
// MUI / Tailwind utility classes, this V3 component uses plain inline
// styles and the V3 design tokens (--ink, --card, --font-sans, --accent).
//
// Why this exists:
//   The V3 chat-message list previously rendered ONLY the streaming chrome
//   (animated hairline + step pill + elapsed timer) until the entire bot
//   response landed in state.messages at sendMessage.fulfilled. The slice's
//   `streamingText` field — populated per token via appendStreamingToken —
//   was never consumed by V3, so users saw only the chrome with no progress
//   text. V1 and V2 both subscribe to `threadBuffer.streamingText` and
//   render it through a streaming markdown sibling; this component is V3's
//   equivalent.
//
// Handoff pattern (mirrors V2 exactly):
//   • isLoading && !streamingText → render StreamingStateV3 chrome (V3 hub
//     handles this branch — this component is NOT rendered).
//   • isLoading && streamingText  → render this component INSTEAD of the
//     chrome. Once the first token arrives the timer is irrelevant; what
//     the user cares about is time-to-first-token, not time-to-completion.
//
// Parse safety:
//   The bot may be partway through emitting a `<list-ref>` tag at the moment
//   we render. We truncate the buffer at the `safe` portion via
//   trimUnclosedListRefTail so a half-open tag never reaches the markdown
//   parser. Once the tag closes (or the stream ends) the cut moves forward
//   and the chip materialises.
//
// Visual parity:
//   We import the same buildMarkdownComponents factory used by
//   ChatBotMessageV3. This guarantees the prose renders with identical p /
//   ul / ol / li / code / table / strong / cite overrides — no visual pop
//   when the stream ends and the final message replaces this surface.
//
// Accessibility:
//   The outer wrapper is aria-live="polite" so screen-reader users hear
//   the growing prose as new tokens arrive (without interrupting other
//   speech). aria-atomic="false" lets readers announce only the appended
//   delta rather than re-read the whole buffer on every chunk.
//
// No MUI, no @emotion, no `sx`, no `dt` tokens, no useRedesignSetup, no
// RedesignXxx components.
import React, { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { trimUnclosedListRefTail } from "../../../../shared/chatBot/streamBotMessage";
import useTranslation from "../../../../shared/hooks/useTranslation";
import MidasLogoV3 from "./MidasLogoV3";
import { buildMarkdownComponents } from "./markdownComponentsV3";

export interface StreamingMarkdownV3Props {
  /** Live streaming buffer (may be partial, may end mid-tag). */
  text: string;
  /** Epoch ms when the stream began — drives the dotted timestamp. */
  startedAt: number;
}

const formatTime = (ts: number): string => {
  const d = new Date(ts);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const StreamingMarkdownV3: React.FC<StreamingMarkdownV3Props> = ({
  text,
  startedAt,
}) => {
  const { translate } = useTranslation("chatBot");

  // Trim any unclosed <list-ref ...> at the buffer tail so the markdown
  // parser never sees a half-open tag mid-stream. The `tail` portion is
  // dropped from this render; it will reappear once the bot closes the tag.
  const safeText = useMemo(() => trimUnclosedListRefTail(text).safe, [text]);

  // We don't have an artifact-active page in the streaming surface (the
  // user can't have opened a citation yet — the stream is mid-flight), so
  // resolveActive is a no-op constant. The markdown renderers happily
  // render <CiteV3> chips in their default (inactive) state.
  const resolveActive = useMemo(() => () => false, []);

  // Use the SAME markdown component factory the final message uses. This is
  // the hard requirement of the streaming-parity fix: no visual pop when the
  // stream ends and ChatBotMessageV3 takes over.
  const markdownComponents = useMemo(
    () => buildMarkdownComponents(resolveActive),
    [resolveActive]
  );

  const streamingLabel = translate("midas.v3.streaming.label", {
    defaultValue: "streaming",
  }) as string;

  return (
    <div
      aria-live="polite"
      aria-atomic="false"
      style={{
        marginBottom: 22,
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      {/* Header strip — matches the final-message header from
          ChatBotMessageV3 so the transition from streaming → final does not
          shift the surrounding chrome. Only difference: the streaming chip
          is "STREAMING" in accent colour (vs. "REPLIED" in faint) so the
          state is visible while it lasts. */}
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <MidasLogoV3 size={26} streaming halo />
        <span
          style={{
            fontWeight: 600,
            fontSize: 13,
            color: "var(--ink)",
            fontFamily: "var(--font-sans)",
          }}
        >
          Midas
        </span>
        <span
          className="mono"
          style={{
            fontSize: 11,
            color: "var(--accent)",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            fontWeight: 600,
          }}
        >
          {streamingLabel}
        </span>
        <span
          className="mono"
          aria-hidden="true"
          style={{
            fontSize: 11,
            color: "var(--faint)",
            letterSpacing: "0.04em",
          }}
        >
          · {formatTime(startedAt)}
        </span>
      </div>

      {/* Body — the progressive prose. We render directly (no ResponseCardV3
          chrome around it) so the visual weight matches the streaming chrome
          we just replaced; once the message finalises, ChatBotMessageV3
          remounts it inside the full ResponseCardV3 card. The user perceives
          this as "text appears, then settles into a card" — preferable to
          forcing the user to wait for the card chrome before any text shows. */}
      <div
        style={{
          padding: "0 2px",
          fontSize: 14,
          lineHeight: 1.5,
          color: "var(--ink)",
          fontFamily: "var(--font-sans)",
        }}
      >
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
          {safeText}
        </ReactMarkdown>
      </div>
    </div>
  );
};

export default StreamingMarkdownV3;
